// SizesSection — athlete apparel + accessory sizes.
//
// Access rules (enforced server-side; mirrored here so the UI never asks
// an endpoint it knows will 403):
//   - View: head coach, the athlete themselves, assigned coach only.
//   - Edit: head coach + athlete. Assigned coaches see read-only inputs
//     (sizing updates should come from the athlete, not a guess).
//
// Lives in the shared components folder so both the athlete's "Personal
// Data" page and anywhere else that wants to render the card can pull
// the same thing — single source of truth for the form shape and the
// save flow.
import React, { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Ruler } from "@phosphor-icons/react";
import { api, formatApiError } from "@/lib/api";

const SIZE_FIELDS = [
  { key: "clothing", label: "Clothing (shirt / top)", placeholder: "e.g. M, L, 10" },
  { key: "shoe", label: "Shoe", placeholder: "e.g. US 9.5" },
  { key: "neck", label: "Neck", placeholder: "e.g. 15 in" },
  { key: "wrist", label: "Wrist", placeholder: "e.g. 6.75 in" },
  { key: "ring", label: "Ring", placeholder: "e.g. US 7" },
];

export default function SizesSection({ athleteId, viewer, athleteCoachId, athleteName }) {
  const [sizes, setSizes] = useState(null);
  const [saving, setSaving] = useState(false);

  const canView = (
    viewer.role === "admin"
    || viewer.id === athleteId
    || (viewer.role === "coach" && athleteCoachId === viewer.id)
  );
  const canEdit = viewer.role === "admin" || viewer.id === athleteId;
  const isSelf = viewer.id === athleteId;

  useEffect(() => {
    if (!canView) return;
    (async () => {
      try {
        const { data } = await api.get(`/users/${athleteId}/sizes`);
        setSizes(data);
      } catch (err) {
        // 403 = coach-not-assigned; silent — we just don't show the card.
        if (err?.response?.status !== 403) toast.error(formatApiError(err));
      }
    })();
  }, [athleteId, canView]);

  if (!canView || !sizes) return null;

  const save = async () => {
    setSaving(true);
    try {
      const body = Object.fromEntries(SIZE_FIELDS.map((f) => [f.key, sizes[f.key] || ""]));
      const { data } = await api.put(`/users/${athleteId}/sizes`, body);
      setSizes(data);
      toast.success("Sizes saved");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const updatedLabel = sizes.updated_at
    ? new Date(sizes.updated_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })
    : null;

  return (
    <Card className="hs-card p-5 sm:p-6" data-testid="sizes-section">
      <div className="flex items-center gap-2">
        <div className="h-9 w-9 rounded-xl grid place-items-center bg-sage text-moss">
          <Ruler size={18} weight="bold" />
        </div>
        <div>
          <h2 className="font-heading text-xl font-semibold">Sizes</h2>
          <p className="text-xs text-ink-3 mt-0.5">
            {isSelf
              ? "Keep these up to date so coaches can order gear in your size."
              : `Coach-visible sizing for ${athleteName}. Only ${athleteName.split(" ")[0]} and the head coach can edit.`}
          </p>
        </div>
      </div>

      <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
        {SIZE_FIELDS.map((f) => (
          <div key={f.key}>
            <Label className="hs-label">{f.label}</Label>
            <input
              type="text"
              value={sizes[f.key] || ""}
              onChange={(e) => setSizes({ ...sizes, [f.key]: e.target.value })}
              placeholder={f.placeholder}
              readOnly={!canEdit}
              className="mt-1 h-11 w-full rounded-xl border border-line bg-bone px-3 font-data text-sm focus:outline-none focus:ring-2 focus:ring-moss/30 disabled:opacity-60 read-only:bg-muted2 read-only:cursor-default"
              data-testid={`size-input-${f.key}`}
            />
          </div>
        ))}
      </div>

      {canEdit ? (
        <div className="mt-5 flex items-center justify-between flex-wrap gap-3">
          <div className="text-xs text-ink-3">
            {updatedLabel ? `Last updated ${updatedLabel}` : "Not saved yet"}
          </div>
          <Button
            onClick={save}
            disabled={saving}
            className="rounded-xl bg-moss hover:bg-moss/90 text-white"
            data-testid="sizes-save"
          >
            {saving ? "Saving…" : "Save sizes"}
          </Button>
        </div>
      ) : (
        updatedLabel && (
          <div className="mt-5 text-xs text-ink-3">Last updated by athlete on {updatedLabel}</div>
        )
      )}
    </Card>
  );
}
